import React from "react";
import { GreyCard, InfoBox, WarningBox, ProhibitedItem } from "./UI";

export default function Modules() {
  return (
    <div className="sz-page-section sz-page-section-alt">
      <div className="container">
        {/* Timeline Sharing */}
        <h2 className="sz-plain-title mb-1">
          Timeline Sharing — Create and Share Your Business Presence
        </h2>
        <p>
          The Timeline is the most dynamic and vital section of the Selectezi
          platform. It functions as your company newsfeed, allowing you to
          post updates, announcements, promotions, and milestones to your
          verified connections.
        </p>
        <p className="mb-4">
          Your timeline reflects your business activity, professionalism, and
          engagement, helping others understand your growth and credibility.
        </p>

        <div className="sz-outer-card">
        <h5 className="fw-bold mb-2">What You Can Share on the Timeline</h5>
        <p className="mb-2">You may post any business related content, such as:</p>
        <div className="sz-share-row"><strong>Product News:</strong> New products, updates, or improvements to your offerings.</div>
        <div className="sz-share-row"><strong>Sales and Offers:</strong> Special promotions, festive discounts, or flash sales.</div>
        <div className="sz-share-row"><strong>Company News:</strong> Launches of new offices, collaborations, or partnerships.</div>
        <div className="sz-share-row"><strong>Milestones and Awards:</strong> Recognitions, certifications, or achievements.</div>
        <div className="sz-share-row"><strong>Business Insights:</strong> Industry related news or professional updates.</div>
        <p className="fw-bold mt-3 mb-0">
          Remember: Selectezi is a professional business platform, every post
          must serve a clear business purpose.
        </p>

        <hr className="sz-divider mb-3 mt-3" />
        <h5 className="fw-bold mb-2">Category Based Sharing System</h5>
        <p>
          Each user belongs to a specific business category, and timeline
          visibility is governed accordingly. This ensures that all
          interactions remain relevant and professional between suitable
          business types.
        </p>
        <p className="mb-0">
          If a user wishes to reach a broader audience across multiple
          categories, they can upgrade to a higher membership plan, Basic,
          Moderate, or Premium, which allows cross-category interaction
          (subject to approval).
        </p>

        <hr className="sz-divider mb-3 mt-3" />
        <h5 className="fw-bold mb-2">Content Moderation Process</h5>
        <p className="mb-2">
          Every post you share undergoes Selectezi's content moderation
          system before becoming public. This ensures all posts meet
          platform standards and maintain a professional environment.
        </p>
        <p className="fw-semibold mb-2">The system automatically detects:</p>
        <ul className="sz-bullet-list">
          <li>Spam or duplicate promotional content</li>
          <li>Off-topic or non-business content</li>
          <li>Abusive or false claims</li>
          <li>Low quality or inappropriate photos</li>
        </ul>
        <p className="mb-0">
          Once approved, your post becomes visible on your timeline and
          accessible to your verified network. If your post violates policy,
          it will be rejected or marked for revision. Repeated violations can
          lead to account suspension.
        </p>
        </div>
        <div className="mb-5" />

        {/* Messenger */}
        <h2 className="sz-plain-title mb-1 ms-0">Messenger Chat System</h2>
        <p className="mb-4">
          The Messenger Tool enables verified businesses to communicate
          directly and securely. It offers a confidential space for
          discussions on business proposals, partnerships, transactions, or
          service inquiries.
        </p>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <GreyCard title="Purpose of Messenger">
              <p className="mb-2">Messenger is strictly for business communication, including:</p>
              <ul className="mb-0">
                <li>B2B and B2C interactions</li>
                <li>Sending quotations or proposals</li>
                <li>Partnership discussions</li>
                <li>Customer or supplier inquiries</li>
              </ul>
            </GreyCard>
          </div>
          <div className="col-md-6">
            <GreyCard title="Guidelines for Using Messenger">
              <p className="mb-2">
                To maintain a clean and professional chat environment, users
                must follow these rules:
              </p>
              <ul className="mb-0">
                <li><strong>No Spamming:</strong> Avoid repeated or unsolicited messages.</li>
                <li><strong>No Unverified Links:</strong> Refrain from sending potentially harmful external links.</li>
                <li><strong>Respect Privacy:</strong> Never request personal or financial details.</li>
                <li><strong>No Abusive or Irrelevant Messages:</strong> Violations will lead to warnings or bans.</li>
              </ul>
            </GreyCard>
          </div>
        </div>
        <p className="mb-5">
          All chat activity is monitored under Selectezi's Professional Use
          Policy to ensure secure and respectful communication.
        </p>

        {/* Advertisements */}
        <h2 className="sz-plain-title mb-1">
          Advertisements (Ad Module): Promote Your Business Professionally
        </h2>
        <p>
          The Advertisement Module allows businesses to promote their
          products, services, offers, or events to their target audience
          within Selectezi.
        </p>
        <p className="mb-4">
          You can create, publish, and manage ads directly from your
          dashboard, with control over budget, duration, and audience
          targeting.
        </p>

        <div className="sz-outer-card">
        <h5 className="fw-bold mb-2" style={{ color: "var(--sz-primary)" }}>Ad Setup Process</h5>
        <p className="mb-2">To create an advertisement:</p>
        <ol className="sz-bullet-list mb-0">
          <li>Go to your Ad Section in the dashboard.</li>
          <li>Click Create Ad and choose the Ad Type (Product, Service, Offer, or Event).</li>
          <li>Set your Budget and Duration.</li>
          <li>Upload high quality photos (JPG or PNG, under 2MB).</li>
          <li>Write a clear, concise Ad Description.</li>
          <li>Submit your ad for review.</li>
        </ol>
        </div>
        <p className="mb-4 mt-3">
          Every ad goes through a manual review by Selectezi's team to ensure
          authenticity and compliance.
        </p>

        <h5 className="fw-bold mb-2" style={{ color: "var(--sz-danger-text)" }}>
          Prohibited Ad Content
        </h5>
        <p className="mb-3">Ads containing the following content will be rejected immediately:</p>
        <ul className="sz-bullet-list">
          <li>
            <ProhibitedItem>Adult, violent, or inappropriate material</ProhibitedItem>
          </li>
          <li>
            <ProhibitedItem>Political or religious campaigns</ProhibitedItem>
          </li>
          <li>
            <ProhibitedItem>Misleading or false promotions</ProhibitedItem>
          </li>
          <li>
            <ProhibitedItem>Disrespectful or harmful messages toward individuals or groups</ProhibitedItem>
          </li>
        </ul>
        <WarningBox>
          Violation of these terms may result in penalties or account
          suspension.
        </WarningBox>

        {/* Business Connections */}
        <h2 className="sz-plain-title mt-5 mb-1">Business Connections</h2>
        <p className="mb-4">
          Selectezi encourages users to build genuine business connections
          within their verified categories. This system protects the
          professional integrity and data security of all users.
        </p>
        <div className="sz-intro-card" style={{ margin: 0 }}>
          <h5 className="fw-bold mb-2" style={{ color: "var(--sz-primary)" }}>Connection Rules</h5>
          <p className="mb-3">
            Each connection request is automatically checked by the system.
            If a user tries to connect outside their permitted category, the
            system blocks or rejects the request.
          </p>
          <hr className="sz-divider mb-3" />
          <h5 className="fw-bold mb-2" style={{ color: "var(--sz-primary)" }}>Why These Rules Are Important</h5>
          <p className="mb-2">This framework helps prevent:</p>
          <ul className="sz-bullet-list">
            <li>Data misuse between unrelated industries</li>
            <li>Unnecessary or irrelevant networking</li>
            <li>Unprofessional communication</li>
          </ul>
          <InfoBox bold>
            By enforcing these boundaries, Selectezi ensures that every
            connection is genuine, verified, and purposeful.
          </InfoBox>
        </div>
      </div>
    </div>
  );
}
